import React, { useRef, useState } from 'react';

function BeforeAfterComparison({ beforeImage, afterImage, beforeLabel = 'Before', afterLabel = 'After' }) {
  const [viewMode, setViewMode] = useState('split');
  const [splitOffset, setSplitOffset] = useState(50);
  const [blendOpacity, setBlendOpacity] = useState(0.5);
  const splitContainerRef = useRef(null);
  const isDragging = useRef(false);

  if (!beforeImage || !afterImage) {
    return null;
  }

  const updateSplitOffset = (clientX) => {
    if (!splitContainerRef.current) return;
    const rect = splitContainerRef.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setSplitOffset(Math.min(100, Math.max(0, pct)));
  };

  const handleSplitPointerDown = (event) => {
    isDragging.current = true;
    updateSplitOffset(event.clientX);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleSplitPointerMove = (event) => {
    if (!isDragging.current) return;
    updateSplitOffset(event.clientX);
  };

  const handleSplitPointerUp = (event) => {
    isDragging.current = false;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return (
    <div className="comparison-section">
      <div className="comparison-tabs">
        <button
          type="button"
          className={`tool-tab${viewMode === 'split' ? ' active' : ''}`}
          onClick={() => setViewMode('split')}
        >
          Split View
        </button>
        <button
          type="button"
          className={`tool-tab${viewMode === 'side-by-side' ? ' active' : ''}`}
          onClick={() => setViewMode('side-by-side')}
        >
          Side by Side
        </button>
        <button
          type="button"
          className={`tool-tab${viewMode === 'blend' ? ' active' : ''}`}
          onClick={() => setViewMode('blend')}
        >
          Blend
        </button>
      </div>

      {viewMode === 'split' && (
        <div
          ref={splitContainerRef}
          className="split-viewer-container"
          onPointerDown={handleSplitPointerDown}
          onPointerMove={handleSplitPointerMove}
          onPointerUp={handleSplitPointerUp}
          style={{ cursor: 'ew-resize' }}
        >
          <div className="split-label-indicator before">{beforeLabel}</div>
          <div className="split-label-indicator after">{afterLabel}</div>
          <div className="split-image-layer">
            <img src={beforeImage} alt={beforeLabel} />
          </div>
          <div
            className="split-image-layer split-after-layer"
            style={{ clipPath: `inset(0 0 0 ${splitOffset}%)` }}
          >
            <img src={afterImage} alt={afterLabel} />
          </div>
          <div className="split-divider-line" style={{ left: `${splitOffset}%` }} />
          <div className="split-divider-handle" style={{ left: `${splitOffset}%` }}>
            ⇄
          </div>
        </div>
      )}

      {viewMode === 'side-by-side' && (
        <div className="side-by-side-container">
          <div className="side-card">
            <div className="side-card-header">
              <span>{beforeLabel}</span>
            </div>
            <div className="side-card-body relative-body">
              <img src={beforeImage} alt={beforeLabel} />
            </div>
          </div>
          <div className="side-card">
            <div className="side-card-header">
              <span>{afterLabel}</span>
            </div>
            <div className="side-card-body relative-body">
              <img src={afterImage} alt={afterLabel} />
            </div>
          </div>
        </div>
      )}

      {viewMode === 'blend' && (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
          <div className="blend-viewer-container">
            <img src={beforeImage} alt={beforeLabel} className="blend-before" />
            <img
              src={afterImage}
              alt={afterLabel}
              className="blend-after"
              style={{ opacity: blendOpacity }}
            />
          </div>
          <div className="slider-group" style={{ width: '100%', maxWidth: '500px' }}>
            <div className="slider-header">
              <span>{beforeLabel}</span>
              <span>Blend: {Math.round(blendOpacity * 100)}%</span>
              <span>{afterLabel}</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={blendOpacity}
              onChange={(e) => setBlendOpacity(parseFloat(e.target.value))}
              className="custom-range"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default BeforeAfterComparison;
